import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  ArrowLeft, 
  RotateCcw, 
  ArrowRightLeft, 
  Scale, 
  CheckCircle2, 
  TrendingUp, 
  HelpCircle 
} from 'lucide-react';

export const UnitConverter = () => {
  const { withAd, toast } = useAdRedirect();
  const [category, setCategory] = useState('Length');
  const [valInput, setValInput] = useState('1');

  // Specific state per category
  const [lengthFrom, setLengthFrom] = useState('Meters');
  const [lengthTo, setLengthTo] = useState('Feet');

  const [weightFrom, setWeightFrom] = useState('Kilograms');
  const [weightTo, setWeightTo] = useState('Pounds');

  const [tempFrom, setTempFrom] = useState('Celsius');
  const [tempTo, setTempTo] = useState('Fahrenheit');

  const [areaFrom, setAreaFrom] = useState('Square Meters');
  const [areaTo, setAreaTo] = useState('Square Feet');

  const [volFrom, setVolFrom] = useState('Liters');
  const [volTo, setVolTo] = useState('Gallons');

  const [speedFrom, setSpeedFrom] = useState('Kilometers per Hour');
  const [speedTo, setSpeedTo] = useState('Miles per Hour');

  // Conversion Tables Base Parity
  const unitsData = {
    Length: {
      base: 'Meters',
      items: {
        'Meters': 1,
        'Kilometers': 0.001,
        'Centimeters': 100,
        'Millimeters': 1000,
        'Miles': 0.000621371,
        'Yards': 1.09361,
        'Feet': 3.28084,
        'Inches': 39.3701
      }
    },
    Weight: {
      base: 'Kilograms',
      items: {
        'Kilograms': 1,
        'Grams': 1000,
        'Milligrams': 1000000,
        'Metric Tons': 0.001,
        'Pounds': 2.20462,
        'Ounces': 35.274
      }
    },
    Area: {
      base: 'Square Meters',
      items: {
        'Square Meters': 1,
        'Square Kilometers': 0.000001,
        'Hectares': 0.0001,
        'Acres': 0.000247105,
        'Square Feet': 10.7639,
        'Square Inches': 1550
      }
    },
    Volume: {
      base: 'Liters',
      items: {
        'Liters': 1,
        'Milliliters': 1000,
        'Cubic Meters': 0.001,
        'Gallons': 0.264172,
        'Quarts': 1.05669,
        'Pints': 2.11338,
        'Fluid Ounces': 33.814
      }
    },
    Speed: {
      base: 'Kilometers per Hour',
      items: {
        'Kilometers per Hour': 1,
        'Miles per Hour': 0.621371,
        'Meters per Second': 0.277778,
        'Knots': 0.539957
      }
    }
  };

  const getActiveFrom = () => {
    switch(category) {
      case 'Length': return lengthFrom;
      case 'Weight': return weightFrom;
      case 'Temperature': return tempFrom;
      case 'Area': return areaFrom;
      case 'Volume': return volFrom;
      case 'Speed': return speedFrom;
      default: return 'Meters';
    }
  };

  const getActiveTo = () => {
    switch(category) {
      case 'Length': return lengthTo;
      case 'Weight': return weightTo;
      case 'Temperature': return tempTo;
      case 'Area': return areaTo;
      case 'Volume': return volTo;
      case 'Speed': return speedTo;
      default: return 'Feet';
    }
  };

  const setActiveFrom = (v) => {
    switch(category) {
      case 'Length': setLengthFrom(v); break;
      case 'Weight': setWeightFrom(v); break;
      case 'Temperature': setTempFrom(v); break;
      case 'Area': setAreaFrom(v); break;
      case 'Volume': setVolFrom(v); break;
      case 'Speed': setSpeedFrom(v); break;
      default: break;
    }
  };

  const setActiveTo = (v) => {
    switch(category) {
      case 'Length': setLengthTo(v); break;
      case 'Weight': setWeightTo(v); break;
      case 'Temperature': setTempTo(v); break;
      case 'Area': setAreaTo(v); break;
      case 'Volume': setVolTo(v); break;
      case 'Speed': setSpeedTo(v); break;
      default: break;
    }
  };

  const handleSwap = () => {
    const f = getActiveFrom();
    const t = getActiveTo();
    setActiveFrom(t);
    setActiveTo(f);
  };

  // Perform Calculation
  const calculateConversion = () => {
    let num = parseFloat(valInput);
    if (isNaN(num)) return { result: 0, formula: 'Invalid Input' };

    let f = getActiveFrom();
    let t = getActiveTo();

    if (f === t) return { result: num, formula: 'Identical units (x * 1)' };

    if (category === 'Temperature') {
      if (f === 'Celsius' && t === 'Fahrenheit') {
        return { result: parseFloat(((num * 9/5) + 32).toFixed(2)), formula: '(°C × 9/5) + 32 = °F' };
      }
      if (f === 'Fahrenheit' && t === 'Celsius') {
        return { result: parseFloat(((num - 32) * 5/9).toFixed(2)), formula: '(°F - 32) × 5/9 = °C' };
      }
      if (f === 'Celsius' && t === 'Kelvin') {
        return { result: parseFloat((num + 273.15).toFixed(2)), formula: '°C + 273.15 = K' };
      }
      if (f === 'Kelvin' && t === 'Celsius') {
        return { result: parseFloat((num - 273.15).toFixed(2)), formula: 'K - 273.15 = °C' };
      }
      if (f === 'Fahrenheit' && t === 'Kelvin') {
        return { result: parseFloat(((num - 32) * 5/9 + 273.15).toFixed(2)), formula: '(°F - 32) × 5/9 + 273.15 = K' };
      }
      if (f === 'Kelvin' && t === 'Fahrenheit') {
        return { result: parseFloat(((num - 273.15) * 9/5 + 32).toFixed(2)), formula: '(K - 273.15) × 9/5 + 32 = °F' };
      }
    }

    // Standard ratio
    const pool = unitsData[category].items;
    const rateFrom = pool[f];
    const rateTo = pool[t];

    let inBase = num / rateFrom;
    let finalVal = inBase * rateTo;

    // formatting
    let result = parseFloat(finalVal.toFixed(6));
    let formula = `Multiply by (${(rateTo / rateFrom).toFixed(4)})`;

    return { result, formula };
  };

  const conv = calculateConversion();

  const handleReset = () => {
    setValInput('1');
  };

  const activeUnitsList = category === 'Temperature' ? ['Celsius', 'Fahrenheit', 'Kelvin'] : Object.keys(unitsData[category].items);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Unit Converter Online - Length, Weight & More" 
        description="Convert length, weight, temperature, area, volume, and speed units instantly. Free universal conversion calculator with formula guides."
        keywords="unit converter, universal converter, metric to imperial, kg to lbs, km to miles, celsius to fahrenheit, online conversion"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Unit Converter</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Universal Unit Converter & Metric System
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Instantly convert length, mass, temperature, and area dimensions with transparent mathematical formula breakdowns.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Scale size={18} style={{ color: 'var(--primary)' }} /> Select Category
            </h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Clear
            </button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
            {['Length', 'Weight', 'Temperature', 'Area', 'Volume', 'Speed'].map(cat => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                style={{ padding: '8px 14px', borderRadius: '10px', border: 'none', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', background: category === cat ? 'var(--primary)' : 'rgba(15, 23, 42, 0.04)', color: category === cat ? '#ffffff' : 'var(--text-primary)', transition: 'var(--transition-smooth)' }}
              >
                {cat}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Value to Convert</label>
              <input 
                type="number" 
                value={valInput} 
                onChange={(e) => setValInput(e.target.value)}
                placeholder="1"
                style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: '1px solid var(--border-color)', fontSize: '1.1rem', fontWeight: 700, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>From Unit</label>
                <select 
                  value={getActiveFrom()} 
                  onChange={(e) => setActiveFrom(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  {activeUnitsList.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>

              <button 
                type="button" 
                onClick={handleSwap}
                style={{ alignSelf: 'flex-end', background: 'rgba(5, 117, 251, 0.1)', color: 'var(--primary)', border: 'none', padding: '12px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Swap Units"
              >
                <ArrowRightLeft size={18} />
              </button>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>To Unit</label>
                <select 
                  value={getActiveTo()} 
                  onChange={(e) => setActiveTo(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  {activeUnitsList.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={18} style={{ color: '#10b981' }} /> Conversion Dashboard
            </h3>

            <div>
              {/* Primary Display */}
              <div style={{ background: 'var(--bg-secondary)', padding: '28px', borderRadius: '16px', border: '1px solid var(--border-color)', textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {valInput || 0} {getActiveFrom()} equals
                </div>
                <div style={{ fontSize: '2.8rem', fontWeight: 900, color: 'var(--primary)', lineHeight: 1.1, marginBottom: '6px', wordBreak: 'break-word' }}>
                  {conv.result} {getActiveTo()}
                </div>
              </div>

              {/* Mathematical Formula Banner */}
              <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10b981', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ color: '#10b981' }}>
                  <HelpCircle size={20} />
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Mathematical Formula</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#10b981' }}>{conv.formula}</div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '28px', textAlign: 'center' }}>
              <AdToast message={toast} />
              <button 
                onClick={withAd('unit-convert-btn', () => alert("Conversion locked client-side."))}
                style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '12px 28px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition-smooth)' }}
              >
                Log Metric Unit
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="unit-converter" />
    </div>
  );
};

export default UnitConverter;
